// ORCA360 - Mobile Debriefing Guide Viewer
// Short guide (default) with toggle to full accordion view, optimized for mobile/PWA

import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { ChevronDown, ChevronRight, AlertTriangle } from 'lucide-react';
import { ShortDebriefingGuide, GuideViewToggle } from '../debriefing';
import type {
  DebriefingGuideRow,
  EventAnalysis,
  UASAnalysis,
  TEMEventType,
} from '../../types/debriefing-guide';
import type { DebriefingGuideView } from '../../lib/short-debriefing-guide.types';

const EVENT_COLORS: Record<TEMEventType, string> = {
  Threat: 'bg-amber-50 border-amber-300 text-amber-800',
  Error: 'bg-red-50 border-red-300 text-red-800',
  UAS: 'bg-purple-50 border-purple-300 text-purple-800',
  Resilience: 'bg-green-50 border-green-300 text-green-800',
};

interface DebriefingMobileViewProps {
  sessionId: string;
}

export default function DebriefingMobileView({ sessionId }: DebriefingMobileViewProps) {
  const [guide, setGuide] = useState<DebriefingGuideRow | null>(null);
  const [instructorNotes, setInstructorNotes] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [view, setView] = useState<DebriefingGuideView>('short');
  const [expanded, setExpanded] = useState<{ partA: boolean; partB: boolean; partC: boolean; partD: boolean }>({
    partA: false,
    partB: true,
    partC: false,
    partD: false,
  });

  useEffect(() => {
    loadGuide();
  }, [sessionId]);

  async function loadGuide() {
    setLoading(true);
    try {
      // Load guide and session instructor notes in parallel
      const [guideResult, sessionResult] = await Promise.all([
        supabase
          .from('debriefing_guides')
          .select('*')
          .eq('session_id', sessionId)
          .order('created_at', { ascending: false })
          .limit(1)
          .single(),
        supabase
          .from('sessions')
          .select('instructor_notes')
          .eq('id', sessionId)
          .single(),
      ]);

      if (guideResult.error) throw guideResult.error;
      setGuide(guideResult.data);
      setInstructorNotes(sessionResult.data?.instructor_notes ?? null);
    } catch (err: unknown) {
      setError((err as Error).message || 'Failed to load debriefing guide');
    } finally {
      setLoading(false);
    }
  }

  const toggle = (key: 'partA' | 'partB' | 'partC' | 'partD') => setExpanded(prev => ({ ...prev, [key]: !prev[key] }));

  const handleViewChange = (newView: DebriefingGuideView) => {
    setView(newView);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <div className="w-8 h-8 border-2 border-navy/30 border-t-navy rounded-full animate-spin" />
        <p className="text-sm text-slate-500 mt-3">Loading debriefing guide...</p>
      </div>
    );
  }

  if (error || !guide) {
    return (
      <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
        <AlertTriangle size={32} className="text-amber-500 mb-3" />
        <p className="text-sm text-slate-600">{error || 'No debriefing guide found'}</p>
      </div>
    );
  }

  const partA = guide.part_a;
  const partB = guide.part_b;
  const partC = guide.part_c;
  const partD = guide.part_d;

  return (
    <div className="pb-2">
      {/* Sticky view toggle */}
      <div className="sticky top-0 z-10 bg-white/95 backdrop-blur-sm border-b border-slate-200 px-2 py-2 flex items-center justify-between">
        <GuideViewToggle view={view} onChange={handleViewChange} />
        {view === 'full' && (
          <button
            onClick={() => handleViewChange('short')}
            className="text-xs text-[#0563C1] italic underline min-h-[44px] flex items-center px-2"
          >
            Back to Short Guide
          </button>
        )}
      </div>

      {/* Short Guide View (default) */}
      {view === 'short' && (
        <div className="px-2 pt-2">
          <ShortDebriefingGuide
            guide={guide}
            onGuideChange={() => {}}
            onSwitchToFull={() => handleViewChange('full')}
            readOnly
            sessionId={sessionId}
            instructorNotes={instructorNotes}
          />
        </div>
      )}

      {/* Full Guide View (original accordion) */}
      {view === 'full' && (
        <div className="space-y-0">
          {/* Part A: Session Details */}
          <AccordionSection
            title="Part A: Session Details"
            expanded={expanded.partA}
            onToggle={() => toggle('partA')}
          >
            <div className="space-y-0.5 text-sm">
              <InfoRow label="Airline" value={partA?.airline} />
              <InfoRow label="Aircraft" value={partA?.aircraftType} />
              <InfoRow label="Session" value={partA?.sessionName} />
              <InfoRow label="Date/Time" value={partA?.dateTime} />
              {partA?.participants && (
                <>
                  <InfoRow label="Instructor" value={partA.participants.instructor} />
                  <InfoRow label="Captain" value={partA.participants.captain} />
                  <InfoRow label="First Officer" value={partA.participants.firstOfficer} />
                </>
              )}
            </div>
          </AccordionSection>

          {/* Part B (DB) / Part C (Export): Debrief Structure */}
          <AccordionSection
            title="Debrief Structure & Questions"
            expanded={expanded.partB}
            onToggle={() => toggle('partB')}
            highlight
          >
            {/* Opening Self-Assessment */}
            {partB?.openingSelfAssessment?.prompts?.length > 0 && (
              <div className="mb-3">
                <h4 className="text-xs font-bold text-navy uppercase tracking-wide mb-1">
                  Opening Self-Assessment
                </h4>
                <ul className="space-y-1">
                  {partB.openingSelfAssessment.prompts.map((p: string, i: number) => (
                    <li key={i} className="text-sm text-slate-700 pl-2 border-l-2 border-sky-200">
                      {p}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* UAS Focused RCA */}
            {partB?.uasFocusedRCA && partB.uasFocusedRCA.length > 0 && (
              <div className="mb-3">
                <h4 className="text-xs font-bold text-purple-700 uppercase tracking-wide mb-1">
                  UAS Focused Analysis
                </h4>
                {partB.uasFocusedRCA.map((uas: UASAnalysis, i: number) => (
                  <div key={i} className="p-2 rounded-lg bg-purple-50 border border-purple-200 mb-1.5">
                    <p className="text-sm font-medium text-purple-900 mb-1">{uas.uasDescription}</p>
                    {uas.questionLadder?.length > 0 && (
                      <ul className="space-y-0.5">
                        {uas.questionLadder.map((q: string, j: number) => (
                          <li key={j} className="text-xs text-purple-800 pl-2 border-l-2 border-purple-300">
                            {q}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Event-Focused Exploration */}
            {partB?.eventFocusedExploration?.length > 0 && (
              <div className="mb-3">
                <h4 className="text-xs font-bold text-navy uppercase tracking-wide mb-1">
                  Event Analysis
                </h4>
                {partB.eventFocusedExploration.map((event: EventAnalysis, i: number) => (
                  <div
                    key={i}
                    className={`p-2 rounded-lg border mb-1.5 ${EVENT_COLORS[event.eventType] || 'bg-slate-50 border-slate-200'}`}
                  >
                    <div className="flex items-center gap-1.5 mb-1">
                      <span className="text-xs font-bold px-1.5 py-0.5 rounded-full bg-white/50">
                        #{event.eventNumber}
                      </span>
                      <span className="text-xs font-semibold">{event.eventType}</span>
                      <span className="text-xs opacity-60">{event.timePhase}</span>
                    </div>
                    <p className="text-sm mb-1">{event.factsSummary}</p>
                    {event.facilitatorQuestions?.length > 0 && (
                      <div className="space-y-0.5">
                        {event.facilitatorQuestions.map((q: string, j: number) => (
                          <p key={j} className="text-xs pl-2 border-l-2 border-current/30 italic">
                            {q}
                          </p>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Crew Defined Strategy */}
            {partB?.crewDefinedStrategy?.statement && (
              <div className="p-2 rounded-lg bg-green-50 border border-green-200">
                <h4 className="text-xs font-bold text-green-800 uppercase tracking-wide mb-0.5">
                  Crew Strategy
                </h4>
                <p className="text-sm text-green-900">{partB.crewDefinedStrategy.statement}</p>
              </div>
            )}
          </AccordionSection>

          {/* Part C (DB) / Part D (Export): Supporting Material */}
          <AccordionSection
            title="Supporting Material"
            expanded={expanded.partC}
            onToggle={() => toggle('partC')}
          >
            {partC?.observedEventsTable?.length > 0 && (
              <div className="mb-3">
                <h4 className="text-xs font-bold text-navy uppercase tracking-wide mb-1">
                  Observed Events
                </h4>
                <div className="space-y-1">
                  {partC.observedEventsTable.map((evt, i: number) => (
                    <div key={i} className="text-sm p-1.5 rounded bg-slate-50 border border-slate-200">
                      <div className="flex items-center gap-1.5 mb-0.5">
                        <span className="text-xs font-bold text-slate-500">#{evt.eventNumber}</span>
                        <span className="text-xs text-slate-400">{evt.timePhase}</span>
                      </div>
                      <p className="text-slate-700">{evt.whatHappened}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </AccordionSection>

          {/* Part D (DB) / Part E (Export): Instructor Notes */}
          <AccordionSection
            title="Instructor Notes (Private)"
            expanded={expanded.partD}
            onToggle={() => toggle('partD')}
            muted
          >
            {partD?.calibrationReflections && (
              <div className="mb-3">
                <h4 className="text-xs font-bold text-slate-600 uppercase tracking-wide mb-1">
                  Calibration Reflections
                </h4>
                <p className="text-sm text-slate-600">{partD.calibrationReflections}</p>
              </div>
            )}
            {partD?.facilitationImprovementPoints && (
              <div className="mb-3">
                <h4 className="text-xs font-bold text-slate-600 uppercase tracking-wide mb-1">
                  Improvement Points
                </h4>
                <p className="text-sm text-slate-600">{partD.facilitationImprovementPoints}</p>
              </div>
            )}
            {partD?.iecClassifications?.length > 0 && (
              <div>
                <h4 className="text-xs font-bold text-slate-600 uppercase tracking-wide mb-1">
                  IEC Reflections
                </h4>
                {partD.iecClassifications.map((iec, i: number) => (
                  <div key={i} className="text-sm text-slate-600 mb-1.5 pl-2 border-l-2 border-slate-200">
                    <span className="font-semibold">{iec.iec}</span>: {iec.reflection}
                  </div>
                ))}
              </div>
            )}
          </AccordionSection>

          {/* Return to short guide */}
          <div className="py-4 text-center">
            <button
              onClick={() => handleViewChange('short')}
              className="text-sm text-[#0563C1] italic underline min-h-[44px]"
            >
              Back to Short Guide
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Sub-components ──────────────────────────────────────────────────────

function AccordionSection({
  title,
  expanded,
  onToggle,
  children,
  highlight,
  muted,
}: {
  title: string;
  expanded: boolean;
  onToggle: () => void;
  children: React.ReactNode;
  highlight?: boolean;
  muted?: boolean;
}) {
  return (
    <div
      className="border-b overflow-hidden"
      style={{
        borderColor: highlight ? '#bae6fd' : '#e2e8f0',
        background: highlight ? '#f0f9ff' : muted ? '#fafafa' : '#ffffff',
      }}
    >
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-2 px-2 py-2 text-left min-h-[44px]"
      >
        {expanded ? (
          <ChevronDown size={14} className="text-slate-400 flex-shrink-0" />
        ) : (
          <ChevronRight size={14} className="text-slate-400 flex-shrink-0" />
        )}
        <span
          className="text-sm font-bold"
          style={{ color: highlight ? '#0B2C45' : muted ? '#64748b' : '#0B2C45' }}
        >
          {title}
        </span>
      </button>
      {expanded && (
        <div className="px-2 pb-2 border-t border-slate-100 pt-2">
          {children}
        </div>
      )}
    </div>
  );
}

function InfoRow({ label, value }: { label: string; value?: string }) {
  if (!value || value === 'Not stated in transcript') return null;
  return (
    <p className="text-sm text-slate-700">
      <span className="font-semibold text-slate-500">{label}:</span> {value}
    </p>
  );
}
